import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import { t } from 'i18next';
import { Link } from 'react-router-dom';
import { Filter, FilterActions, FilterContent, FilterSelectNative } from '@/components/custom/filter';
import { useCheckIncludeProfiles } from '@/app/hooks/useAuthorityLevel';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { EssayCard } from './essay-card';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import usePermission from '@/app/hooks/usePermission';
import { DashboardContent } from '@/layouts/dashboard';
import { CardWrapper } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export function Essay(props) {
  const { uri } = props;

  const [courses, setCourses] = useState([]);
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(false);
  const client = useClient();
  const isAdmin = !useCheckIncludeProfiles({ only: ['student'] });
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });
  const [filteredEssays, setFilteredEssays] = useState([]);

  const filters = useFormik({
    initialValues: {
      course: 'blank'
    }
  });

  const getCourses = async () => {
    return await client.fetch('courses/simple');
  };

  const getEssays = async () => {
    setLoading(true);
    try {
      const response = await client.fetch('essays');
      return response;
    } catch (e) {
      throw new Error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    const [courses, essays] = await Promise.all([getCourses(), getEssays()]);
    if (courses.error) {
      toast.error('Erro ao carregar cursos');
    } else {
      setCourses(courses.data);
    }
    if (essays.error) {
      toast.error('Erro ao carregar redações');
    } else {
      setEssays(essays.data);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setFilteredEssays(essays.filter(item => item?.['courses-ids']?.includes(parseInt(filters.values.course)) || filters.values.course === 'blank'));
  }, [filters.values.course, essays]);

  const deleteEssay = async id => {
    const { error } = await client.delete(['essays', id]);
    if (error) {
      toast.error('Erro ao deletar redação');
    } else {
      toast.success('Redação excluida!');
      setEssays(prevState => prevState.filter(item => item.id !== id));
    }
  };

  const handleDeleteEssay = id => {
    Swal.fire({
      title: 'Deseja mesmo excluir esta redação?',
      confirmButtonText: t('button.yes'),
      showDenyButton: true,
      denyButtonText: t('button.no'),
      showCloseButton: true
    }).then(res => {
      if (res.isConfirmed) {
        deleteEssay(id);
      }
    });
  };

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Redação',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Filter variant="plain">
        <FilterContent>
          <FilterSelectNative
            label={t('exams.selectCourse')}
            value={filters.values.course}
            onChange={e => filters.setFieldValue('course', e.target.value)}
            options={courses.map(o => ({
              id: o.id,
              name: o.title
            }))}
          />
        </FilterContent>

        {isStudent && (
          <FilterActions>
            <Button
              wide
              asChild
            >
              <a
                href="/redacao.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Baixar folha de redação
              </a>
            </Button>
          </FilterActions>
        )}

        {usePermission('Criar redação', 'Redação') ? (
          <FilterActions>
            <Button
              wide
              asChild
            >
              <Link to={`/redacao/criar/curso`}>Nova redação</Link>
            </Button>
          </FilterActions>
        ) : null}
      </Filter>

      {loading && <Loader />}

      {filteredEssays?.length === 0 && !loading && <EmptyState type="content" />}

      {!loading && (
        <CardWrapper className="animate-fade-in">
          {filteredEssays?.map(essay => (
            <EssayCard
              essay={essay}
              isAdmin={isAdmin}
              key={essay.id}
              deleteEssay={handleDeleteEssay}
            />
          ))}
        </CardWrapper>
      )}
    </DashboardContent>
  );
}
